import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore'; 

// Modales de UI
import ModalEdicionContrato from '../../ui/ModalEdicionContrato';
import ModalNotificacion from '../../ui/ModalNotificacion'; 
import ModalAnulacionContratos from '../../ui/ModalAnulacionContratos';

// --- HELPERS DE FORMATO ---
const formatCurrency = (val) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val || 0);
const formatPercent = (val) => `${Number(val || 0).toFixed(2).replace('.', ',')}%`;

// --- ICONOS ---
const IconArrowLeft = () => <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
const IconDownload = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const IconTrendingUp = ({ className }) => <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
const IconBuilding = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>;
const IconBriefcase = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>;
const IconMail = () => <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const IconPhone = () => <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81 0.7A2 2 0 0 1 22 16.92z"/></svg>;
const IconInfo = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const IconMessage = () => <svg className="w-4 h-4" viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 1 1-2 2h-3l-4 4z" /></svg>;

const DetalleAumentoArt = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Bypass de roles para asegurar visualización total de los controles comerciales
    const isFullUser = true;
    const state = location.state || {};

    // --- ESTADOS ---
    const [loading, setLoading] = useState(true);
    const [cotizacion, setCotizacion] = useState(null);
    const [fetchedClient, setFetchedClient] = useState(null);
    const [endosoFull, setEndosoFull] = useState(null); 
    const [contractFull, setContractFull] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const captureRef = useRef(null);

    // Modales de control
    const [showContratoModal, setShowContratoModal] = useState(false);
    const [showAnulacionModal, setShowAnulacionModal] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

    const [contratoPrefill, setContratoPrefill] = useState(null);
    const [clientDataForModal, setClientDataForModal] = useState(null);
    const [selectedComparacion, setSelectedComparacion] = useState(null);

    // Estado temporal para guardar los datos del nuevo contrato antes de la anulación
    const [tempContractData, setTempContractData] = useState(null);

    // --- CARGA DE DATOS EN TIEMPO REAL ---
    useEffect(() => {
        let unsubscribeCot = () => {};
        
        const initDataFetch = async () => {
            const { cotizacionId, entidadId, clientId, contratoId, endosoId } = state;
            const finalClienteId = clientId || entidadId; 

            if (!finalClienteId || !cotizacionId) {
                setLoading(false);
                return;
            }

            try {
                const cotRef = doc(db, `public/data/clientes/${finalClienteId}/cotizaciones_art`, cotizacionId);
                
                unsubscribeCot = onSnapshot(cotRef, async (snap) => {
                    if (snap.exists()) {
                        const cData = { id: snap.id, path: snap.ref.path, ...snap.data() };
                        setCotizacion(cData);
                        
                        const parentContratoId = contratoId || cData.contratoOrigenId || cData.contratoVinculadoId;
                        const finalEndosoId = endosoId || cData.endosoVinculadoId;

                        if (parentContratoId && finalEndosoId) {
                            const endosoSnap = await getDoc(doc(db, `public/data/clientes/${finalClienteId}/contratos/${parentContratoId}/aumentos_art`, finalEndosoId));
                            if (endosoSnap.exists()) {
                                setEndosoFull({ id: endosoSnap.id, path: endosoSnap.ref.path, ...endosoSnap.data() });
                            }
                            const conSnap = await getDoc(doc(db, `public/data/clientes/${finalClienteId}/contratos`, parentContratoId));
                            if (conSnap.exists()) setContractFull({ id: conSnap.id, ...conSnap.data() });
                        }
                    }
                    setLoading(false);
                });

                const cliDoc = await getDoc(doc(db, 'public/data/clientes', finalClienteId));
                if (cliDoc.exists()) setFetchedClient({ id: cliDoc.id, ...cliDoc.data() });

            } catch (err) {
                console.error("Error cargando detalle de aumento:", err);
                setLoading(false);
            }
        };

        initDataFetch();
        return () => unsubscribeCot();
    }, [state.cotizacionId, state.entidadId, state.clientId, state.contratoId, state.endosoId]);

    useEffect(() => {
        if (!window.html2canvas) {
            const script = document.createElement('script');
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    const sourceData = useMemo(() => {
        if (!cotizacion && !fetchedClient) return null;
        return { ...cotizacion, ...fetchedClient };
    }, [cotizacion, fetchedClient]);

    const fullName = useMemo(() => {
        if (!sourceData) return loading ? 'Cargando...' : 'SIN NOMBRE';
        const nom = sourceData.clienteNombre || sourceData.nombre || '';
        const ape = sourceData.clienteApellido || sourceData.apellido || '';
        const res = `${nom} ${ape}`.trim(); 
        return res === '' ? (loading ? 'Cargando...' : 'SIN NOMBRE') : res.toUpperCase();
    }, [sourceData, loading]);

    const displayEmail = useMemo(() => {
        if (!sourceData) return 'No informado';
        if (sourceData.clienteEmail) return sourceData.clienteEmail;
        if (Array.isArray(sourceData.emails) && sourceData.emails.length > 0) {
            const first = sourceData.emails[0];
            return (typeof first === 'object' ? (first.valor || first.email) : first) || 'No informado';
        }
        return sourceData.email || 'No informado';
    }, [sourceData]);

    const displayPhone = useMemo(() => {
        if (!sourceData) return 'No informado';
        if (sourceData.clienteTelefono) return sourceData.clienteTelefono;
        if (Array.isArray(sourceData.telefonos) && sourceData.telefonos.length > 0) {
            const first = sourceData.telefonos[0];
            return (typeof first === 'object' ? (first.valor || first.telefono) : first) || 'No informado';
        }
        return sourceData.telefono || 'No informado';
    }, [sourceData]);

    const clientCuit = (sourceData?.clienteCuit || sourceData?.cuit || '-').toString().trim();

    const { ofertas, listaVisual } = useMemo(() => {
        if (!cotizacion?.cotizaciones) return { ofertas: [], listaVisual: [] };
        const validas = cotizacion.cotizaciones.filter(c => 
            parseFloat(c.alicuotaCotizada) > 0 && 
            !['Solicitada', 'Bloqueada', 'No cotiza', 'Stand By'].includes(c.estadoRow || c.estadoCotizacion)
        );
        const invalidas = cotizacion.cotizaciones.filter(c => !validas.includes(c));
        validas.sort((a, b) => parseFloat(a.alicuotaCotizada) - parseFloat(b.alicuotaCotizada));
        return { ofertas: validas, listaVisual: [...validas, ...invalidas] };
    }, [cotizacion?.cotizaciones]);

    useEffect(() => {
        if (ofertas.length > 0 && !selectedComparacion) setSelectedComparacion(ofertas[0]);
    }, [ofertas, selectedComparacion]);

    const masaSalarial = parseFloat(cotizacion?.masaSalarial || 0);
    const empleados = parseInt(cotizacion?.capitas || cotizacion?.cantEmpleados || 1);
    const alicuotaAnterior = parseFloat(cotizacion?.alicuotaActual || 0);
    const alicuotaAumentada = parseFloat(endosoFull?.alicuotaAumentada || 0);

    const stats = useMemo(() => {
        const o = selectedComparacion || (ofertas.length > 0 ? ofertas[0] : null);
        if (!o || !cotizacion) return { costoMensual: 0, variacionOriginal: 0 };
        const variacionOriginal = alicuotaAnterior > 0 ? ((alicuotaAumentada - alicuotaAnterior) / alicuotaAnterior) * 100 : 0;
        return { alicNue: parseFloat(o.alicuotaCotizada), variacionOriginal };
    }, [selectedComparacion, ofertas, alicuotaAnterior, alicuotaAumentada, cotizacion]);

    // --- FLUJO DE EMISIÓN Y ANULACIÓN ---

    const handleOpenContractModal = () => {
        if (!!cotizacion?.nContratoVinculado) return;
        const o = selectedComparacion || (ofertas.length > 0 ? ofertas[0] : null);
        
        setContratoPrefill({
            ciiu: cotizacion.ciiu || contractFull?.ciiu || sourceData?.ciiu || '',
            capitas: cotizacion.capitas || '',
            masaSalarial: cotizacion.masaSalarial || '',
            alicuota: o?.alicuotaCotizada || '', 
            compania: o?.compania || '', 
            vendedor: cotizacion.vendedor || sourceData?.vendedor || '',
            canal: cotizacion.canal || sourceData?.canal || sourceData?.tipoCanal || '',
            canalNombreId: cotizacion.canalNombreId || sourceData?.canalNombreId || '',
            canalNombre: cotizacion.canalNombre || sourceData?.canalNombre || '',
            subcanalNombreId: cotizacion.subcanalNombreId || sourceData?.subcanalNombreId || '',
            subcanalNombre: cotizacion.subcanalNombre || sourceData?.subcanalNombre || '',
            tipoOperacion: 'Emisión por Reemplazo',
            cotizacionId: cotizacion.id, 
            cotizacionPath: cotizacion.path 
        });
        setClientDataForModal({ ...fetchedClient, id: cotizacion.clienteId || fetchedClient?.id });
        setShowContratoModal(true);
    };

    const handleContractSuccess = (msg, contractData) => {
        setShowContratoModal(false);
        setTempContractData(contractData);
        setTimeout(() => setShowAnulacionModal(true), 300);
    };

    const handleAnulacionSuccess = async (msg) => {
        setShowAnulacionModal(false);
        const { cotizacionId, clientId, entidadId } = state;
        const finalClientId = clientId || entidadId || cotizacion?.clienteId;

        if (!finalClientId || !cotizacionId || !tempContractData) return;

        try {
            const cotRef = doc(db, `public/data/clientes/${finalClientId}/cotizaciones_art`, cotizacionId);
            const vinculacion = {
                estado: 'Cerrada Ganada',
                nContratoVinculado: tempContractData.nContratoVinculado || tempContractData.nContrato || 'S/N',
                contratoVinculadoId: tempContractData.contratoVinculadoId || tempContractData.id || '',
                ultimaModificacion: new Date().toISOString()
            };
            
            await updateDoc(cotRef, vinculacion);
            setCotizacion(prev => ({ ...prev, ...vinculacion }));
            setNotification({ 
                show: true, 
                message: `¡Circuito completado! Póliza ${vinculacion.nContratoVinculado} emitida y anterior anulada correctamente.`, 
                type: 'success' 
            });
        } catch (e) {
            console.error("Error al finalizar vinculación de reemplazo:", e);
        }
    };

    if (loading) return <div className="p-20 text-center font-bold text-slate-400 animate-pulse italic uppercase tracking-widest text-[10px]">Sincronizando información del aumento...</div>;
    
    if (!cotizacion) return (
        <div className="p-20 text-center m-10 border-2 border-dashed border-red-200 rounded-2xl bg-red-50/20 animate-fade-in text-left">
            <p className="text-red-500 font-bold uppercase text-lg tracking-tight">Registro No Encontrado</p>
            <p className="text-slate-500 text-sm mt-2">La negociación asociada no se encuentra en el cliente.</p>
            <button onClick={() => navigate(-1)} className="mt-6 px-6 py-2 bg-slate-800 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-700 transition-all">Volver al Tablero</button>
        </div>
    );

    const isContratoGenerado = !!cotizacion.nContratoVinculado || cotizacion.estado === 'Cerrada Ganada';

    return (
        <div className="animate-fade-in p-6 md:p-10 bg-white text-left">
            <div className="max-w-[1600px] mx-auto space-y-6">
                
                {isContratoGenerado && (
                    <div className="bg-blue-600 text-white px-6 py-4 rounded-xl shadow-lg flex items-center justify-between animate-bounce-in border-b-4 border-blue-800 text-left">
                        <div className="flex items-center gap-4 text-left">
                            <div className="bg-white/20 p-2 rounded-lg"><IconInfo /></div>
                            <div className="text-left">
                                <p className="font-bold text-lg leading-none italic">Póliza emitida bajo el número: <span className="font-mono bg-blue-700 px-3 py-1 rounded-md text-white border border-blue-400/50 shadow-inner ml-2">{cotizacion.nContratoVinculado || '---'}</span></p>
                                <p className="text-blue-100 text-[10px] mt-1.5 uppercase tracking-widest font-bold opacity-80">Gestión de aumento cerrada satisfactoriamente</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm sticky top-0 z-10 gap-4 text-left">
                    <div className="flex items-center gap-4 text-left">
                        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500"><IconArrowLeft /></button>
                        <div className="text-left">
                            <div className="flex items-center gap-2 text-left">
                                <h1 className="text-xl font-bold text-slate-800 uppercase italic text-left">{fullName}</h1>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded border uppercase bg-blue-50 text-blue-600 border-blue-100 text-left">CLIENTE</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 text-left">
                                <div className="flex items-center gap-1"><IconMail /> {displayEmail}</div>
                                <div className="flex items-center gap-1 border-l border-slate-200 pl-3"><IconPhone /> {displayPhone}</div>
                            </div>
                        </div>
                    </div>
                    {/* ◄ REFACTORIZACIÓN: Eliminamos completamente el botón de IconTrash y su separador visual */}
                    <div className="flex items-center gap-2 w-full md:w-auto text-left">
                        {isFullUser && (
                            <button 
                                onClick={handleOpenContractModal} 
                                disabled={isContratoGenerado} 
                                className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm shadow-sm transition-all ${isContratoGenerado ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-60' : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'}`}
                            >
                                <IconBriefcase /> {isContratoGenerado ? 'Contrato Emitido' : 'Emitir Anulación por Reemplazo'}
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-left">
                    <div className="space-y-6 text-left">
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm text-left">
                            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4 flex items-center gap-2 text-left"><IconBuilding /> Datos del Riesgo</h3>
                            <div className="space-y-3 text-sm text-left">
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500 text-left">Empleados</span><span className="font-semibold text-slate-800 text-left">{empleados}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500 font-bold uppercase text-[10px] tracking-tight text-left">CIIU</span><span className="font-mono font-bold text-blue-800 text-left">{cotizacion.ciiu || sourceData?.ciiu || '-'}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500 text-left">Masa Salarial</span><span className="font-semibold text-slate-800 text-left">{formatCurrency(masaSalarial)}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500 text-left">Alic. Anterior (%)</span><span className="font-semibold text-slate-800 text-left">{formatPercent(alicuotaAnterior)}</span></div>
                                {/* ◄ SOLICITUD: Retiramos DNI, dejamos únicamente CUIT */}
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500 text-left">CUIT</span><span className="font-semibold text-blue-600 uppercase text-left">{clientCuit}</span></div>
                                
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500 text-left">Canal / Org</span><span className="font-semibold text-slate-800 uppercase text-left">{cotizacion.canal || sourceData?.canal || '-'}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500 text-left">Nombre Canal</span><span className="font-semibold text-slate-800 uppercase text-left">{cotizacion.canalNombre || sourceData?.canalNombre || '-'}</span></div>
                                <div className="flex justify-between pt-2"><span className="text-slate-500 text-left">Subcanal / Asociado</span><span className="font-semibold text-slate-800 uppercase text-left">{cotizacion.subcanalNombre || sourceData?.subcanalNombre || '-'}</span></div>
                            </div>
                            
                            {/* ◄ SOLICITUD: Añadimos responsableEndoso, mesAumento, periodoArca y compania de forma prolija */}
                            <div className="mt-6 pt-6 border-t border-slate-100 text-left">
                                <h4 className="text-xs font-black text-slate-400 uppercase mb-3 tracking-widest text-left">Información del Endoso</h4>
                                <div className="space-y-2 text-xs text-left">
                                    <div className="flex justify-between text-left"><span className="text-slate-400 text-left">N° Endoso:</span><span className="font-bold text-slate-600 font-mono text-left">{endosoFull?.numeroEndoso || 'S/D'}</span></div>
                                    <div className="flex justify-between text-left"><span className="text-slate-400 text-left">Tipo Gestión:</span><span className="font-bold text-orange-600 text-left">Aumento de Tarifa</span></div>
                                    <div className="flex justify-between text-left"><span className="text-slate-400 text-left">Contrato Original:</span><span className="font-bold text-blue-600 font-mono text-left">{contractFull?.nContrato || endosoFull?.nContrato || 'S/D'}</span></div>
                                    <div className="flex justify-between text-left"><span className="text-slate-400 text-left">Responsable:</span><span className="font-bold text-slate-600 text-left">{endosoFull?.responsableEndoso || 'S/D'}</span></div>
                                    <div className="flex justify-between text-left"><span className="text-slate-400 text-left">Mes Aumento:</span><span className="font-bold text-slate-600 font-mono text-left">{endosoFull?.mesAumento || 'S/D'}</span></div>
                                    <div className="flex justify-between text-left"><span className="text-slate-400 text-left">Periodo ARCA:</span><span className="font-bold text-slate-600 font-mono text-left">{endosoFull?.periodoArca || 'S/D'}</span></div>
                                    <div className="flex justify-between text-left"><span className="text-slate-400 text-left">Compañía ART:</span><span className="font-bold text-slate-600 text-left">{endosoFull?.compania || 'S/D'}</span></div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-red-600 to-orange-600 p-6 rounded-xl shadow-lg text-white relative overflow-hidden text-left">
                            <div className="absolute top-0 right-0 p-4 opacity-10 rotate-12"><IconTrendingUp className="w-16 h-16" /></div>
                            <h3 className="text-lg font-bold mb-6 italic text-left">Impacto del Aumento</h3>
                            <div className="grid grid-cols-3 gap-2 mb-6 text-left">
                                <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm text-center">
                                    <p className="text-orange-100 text-[8px] font-bold uppercase mb-1 text-center">Anterior</p>
                                    <p className="text-base font-bold text-white text-center">{formatPercent(alicuotaAnterior)}</p>
                                </div>
                                <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm text-center">
                                    <p className="text-orange-100 text-[8px] font-bold uppercase mb-1 text-center">Aumentada</p>
                                    <p className="text-base font-bold text-white text-center">{formatPercent(alicuotaAumentada)}</p>
                                </div>
                                <div className="bg-white/20 p-2 rounded-lg backdrop-blur-md ring-1 ring-white/40 shadow-xl scale-105 text-center">
                                    <p className="text-white text-[8px] font-black uppercase mb-1 text-center">Aumento</p>
                                    <p className="text-xl font-black text-yellow-300 text-center">+{stats.variacionOriginal.toFixed(1)}%</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-slate-100 to-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm relative text-left">
                            <h3 className="text-xs font-black text-slate-600 uppercase mb-3 flex items-center gap-2 text-left">
                                <IconMessage /> Notas de la Gestión
                            </h3>
                            <div className="bg-white/80 p-4 rounded-lg border border-slate-200 text-sm text-slate-700 leading-relaxed italic text-left">
                                {endosoFull?.observaciones || cotizacion?.observaciones || "Sin observaciones adicionales registradas para este aumento."}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2 space-y-4 text-left">
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 italic text-left">Comparativa de Ofertas (Contra el Aumento)</h3>
                        {listaVisual.map((o, i) => {
                             const isSelected = selectedComparacion === o;
                             
                             // ◄ SOLICITUD: Resolvemos el estado de fila de forma insensible a la propiedad
                             const estadoFila = o.estadoRow || o.estadoCotizacion || 'Cotizada';
                             const isInvalid = ['Solicitada', 'Bloqueada', 'No cotiza', 'Stand By'].includes(estadoFila);
                             
                             const alicuota = parseFloat(o.alicuotaCotizada) || 0;
                             const cMensual = masaSalarial * (alicuota / 100);
                             const cCapita = empleados > 0 ? cMensual / empleados : 0;
                             const cAnual = cMensual * 13;

                            return (
                                <div key={i} onClick={() => !isInvalid && alicuota > 0 && setSelectedComparacion(o)} className={`relative p-5 rounded-xl border transition-all group ${isSelected ? 'bg-blue-50 border-blue-200 shadow-md ring-1 ring-blue-200' : isInvalid ? 'bg-gray-50/50 border-gray-200 opacity-80 cursor-default' : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-md cursor-pointer text-left'}`}>
                                    {o === ofertas[0] && !isInvalid && <div className="absolute -top-3 -right-3 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-3 py-1 rounded-full shadow-sm z-10">✓ MEJOR OPCIÓN</div>}
                                    <div className="flex justify-between items-center mb-4 text-left">
                                        <div className="text-left">
                                            <h4 className="font-bold text-slate-800 text-lg uppercase text-left">{o.compania}</h4>
                                            {isInvalid ? <span className="inline-block px-2.5 py-1 rounded text-[10px] font-black bg-red-100/50 text-red-600 border border-red-200 uppercase mt-1.5">{estadoFila}</span> : <p className="text-xs text-slate-500 text-left">Oferta procesada satisfactoriamente</p>}
                                        </div>
                                    </div>
                                    
                                    {/* ◄ SOLICITUD: Si la cotización tiene estado diferente de 'Cotizada', mostramos en la parte superior derecha el estado de la negociación con estética impecable */}
                                    {(!isInvalid && alicuota > 0) ? (
                                        <div className="text-right">
                                            <p className="text-3xl font-black text-slate-800">{formatPercent(alicuota)}</p>
                                            <p className="text-[10px] text-slate-400 font-bold uppercase text-right">Nueva Propuesta</p>
                                        </div>
                                    ) : (
                                        <div className="text-right">
                                            <p className="text-lg font-black text-slate-500 uppercase">{estadoFila}</p>
                                            <p className="text-[10px] text-slate-400 font-bold uppercase text-right">Estado de Oferta</p>
                                        </div>
                                    )}

                                    {!isInvalid && alicuota > 0 && (
                                        <div className="grid grid-cols-4 gap-4 border-t border-slate-200/60 pt-4 text-left">
                                            <div className="text-left"><p className="text-xs text-slate-400 mb-1 uppercase font-bold text-[10px]">Mensual</p><p className="font-semibold text-slate-700">{formatCurrency(cMensual)}</p></div>
                                            <div className="text-left"><p className="text-xs text-slate-400 mb-1 uppercase font-bold text-[10px]">p/ Cápita</p><p className="font-semibold text-slate-700">{formatCurrency(cCapita)}</p></div>
                                            <div className="text-left"><p className="text-xs text-slate-400 mb-1 uppercase font-bold text-[10px]">Proyección Anual</p><p className="font-semibold text-slate-700">{formatCurrency(cAnual)}</p></div>
                                            <div className="text-right flex items-end justify-end"><button className={`text-sm font-medium ${isSelected ? 'text-blue-600 font-bold' : 'text-slate-400 group-hover:text-blue-500'}`}>{isSelected ? 'Seleccionada' : 'Seleccionar'}</button></div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* MODALES */}
            <ModalEdicionContrato show={showContratoModal} onClose={() => setShowContratoModal(false)} clientData={clientDataForModal} contrato={contratoPrefill} onSuccess={handleContractSuccess} />
            
            <ModalAnulacionContratos 
                show={showAnulacionModal} 
                onClose={() => {
                    setShowAnulacionModal(false);
                    setNotification({ show: true, message: 'Atención: Debe completar la anulación del contrato anterior para finalizar el reemplazo.', type: 'info' });
                }} 
                contrato={contractFull} 
                clientId={fetchedClient?.id || cotizacion?.clienteId} 
                onSuccess={handleAnulacionSuccess} 
            />

            <ModalNotificacion show={notification.show} onClose={() => setNotification({ ...notification, show: false })} message={notification.message} type={notification.type} />

            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                .animate-bounce-in { animation: bounceIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards; }
                @keyframes bounceIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default DetalleAumentoArt;